/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, memo, type FC } from 'react';
import type { ContentProps, TriggerProps } from '@atlaskit/popup/types';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { OnClose } from '../../common/types.tsx';
import { useOpenable } from '../../common/utils/use-openable/index.tsx';
import { MoreNavMenuButtonSubmenu } from './submenu/index.tsx';
import { MoreNavMenuButtonTrigger } from './trigger/main.tsx';
import type { MoreNavMenuButtonProps } from './types.tsx';

/*
 * An icon only button to be used on the right side of nav menu items.
 * Opens a dropdown menu with additional options.
 *
 * @param MenuItems -  Wrap your menu items (e.g. a list of <LinkItem>-s) with <Section isList>, otherwise there will be a11y error about "Missing list markup" / lacking <ul<li> structure.
 */
export const MoreNavMenuButton: FC<MoreNavMenuButtonProps> = memo(
	({ MenuItems, name, onOpen, onClose: onCloseFlyout }) => {
		const { isOpen, onClose: onCloseDropdown, onToggle } = useOpenable();

		const content = useCallback(
			(contentProps: ContentProps) => (
				<MoreNavMenuButtonSubmenu {...contentProps} MenuItems={MenuItems} />
			),
			[MenuItems],
		);

		const onClose = useCallback<OnClose>(
			(e) => {
				try {
					onCloseFlyout?.(e);
				} finally {
					onCloseDropdown();
				}
			},
			// oxlint-disable-next-line react/react-compiler
			[onCloseFlyout, onCloseDropdown],
		);

		const onTriggerClick = useCallback(() => {
			try {
				if (!isOpen) {
					onOpen?.();
				}
			} finally {
				onToggle();
			}
			// oxlint-disable-next-line react/react-compiler
		}, [isOpen, onOpen, onToggle]);

		const trigger = useCallback(
			(triggerProps: TriggerProps) => (
				<MoreNavMenuButtonTrigger
					{...triggerProps}
					name={name}
					onClick={onTriggerClick}
					isSelected={isOpen}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-apps-sidebar-nav4-sidebars-common-core.ui.more-nav-menu-button.more-nav-menu-button-trigger"
				/>
			),
			[isOpen, name, onTriggerClick],
		);

		return (
			<Popup
				content={content}
				isOpen={isOpen}
				onClose={onClose}
				placement="bottom-start"
				// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
				messageId="navigation-apps-sidebar-nav4-sidebars-common-core.ui.more-nav-menu-button.popup"
				messageType="transactional"
				trigger={trigger}
				shouldRenderToParent
			/>
		);
	},
);
